// The user's own email signature, added under every email that leaves from
// their mailbox — quotations, invoices and other documents sent from the app,
// and messages composed or replied to in Mail Inbox.
//
// Built to take a signature pasted from ANYWHERE (Gmail, Outlook, Word, a
// website, a signature generator) with its formatting, links, table layout
// and logo intact. The server cleans what is pasted down to safe email HTML
// (src/shared/email/signature.js) and turns pasted images into public files a
// customer's inbox can load; after Save the box shows exactly what was kept,
// which is what the customer will see.

import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Bold, Italic, Underline, Link2, ImagePlus, Eraser } from 'lucide-react';
import { api } from '@/shared/api/axios';

const MAX_IMAGE_BYTES = 1_000_000;

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/** Older signatures were plain text; show them as lines. */
const toEditorHtml = (stored: string) =>
  /<[a-z][^>]*>/i.test(stored) ? stored : stored.split(/\r?\n/).map(escapeHtml).join('<br>');

const readAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(r.error);
  r.readAsDataURL(file);
});

export function SignatureEditor({ signature, canSend, onSaved }: { signature: string; canSend: boolean; onSaved: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [dirty, setDirty] = useState(false);
  const [empty, setEmpty] = useState(!signature);

  // Load the saved signature into the box (not while the user is editing).
  useEffect(() => {
    if (box.current && !dirty) {
      box.current.innerHTML = toEditorHtml(signature || '');
      setEmpty(!signature);
    }
  }, [signature]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = () => {
    setDirty(true);
    const el = box.current;
    setEmpty(!el || (!el.querySelector('img') && !(el.textContent || '').trim()));
  };

  const insertImages = async (files: File[]) => {
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      if (f.size > MAX_IMAGE_BYTES) { toast.error(`${f.name || 'That image'} is over 1 MB — use a smaller logo.`); continue; }
      const url = await readAsDataUrl(f);
      box.current?.focus();
      document.execCommand('insertHTML', false, `<img src="${url}" alt="" style="max-width:240px;height:auto;">`);
    }
    changed();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    // A copied screenshot/logo arrives as a FILE with no HTML; the browser
    // would drop it. Formatted text (with or without images) pastes normally.
    const files = Array.from(e.clipboardData.files || []).filter((f) => f.type.startsWith('image/'));
    if (files.length && !e.clipboardData.getData('text/html')) {
      e.preventDefault();
      void insertImages(files);
    }
  };

  const cmd = (command: string, value?: string) => {
    box.current?.focus();
    document.execCommand(command, false, value);
    changed();
  };
  const addLink = () => {
    const url = window.prompt('Link address (https://…, mailto:… or tel:…)', 'https://');
    if (url && url !== 'https://') cmd('createLink', url.trim());
  };
  const clearAll = () => {
    if (!window.confirm('Clear the whole signature?')) return;
    if (box.current) box.current.innerHTML = '';
    changed();
  };

  const save = useMutation({
    mutationFn: async () => (await api.put('/api/mailbox/signature', { signature: box.current?.innerHTML || '' })).data,
    onSuccess: (d: { signature: string; warnings?: string[] }) => {
      // Show what the server kept — exactly what customers will see.
      if (box.current) box.current.innerHTML = toEditorHtml(d.signature || '');
      setDirty(false);
      setEmpty(!d.signature);
      if (d.warnings?.length) d.warnings.forEach((w) => toast(w, { icon: '⚠️', duration: 9000 }));
      toast.success(d.signature ? 'Signature saved — it is added to every email you send.' : 'Signature removed.');
      onSaved();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not save the signature.'),
  });
  const test = useMutation({
    mutationFn: async () => (await api.post('/api/mailbox/signature/test')).data,
    onSuccess: (d: any) => toast.success(`Test email sent to ${d.to} — check that inbox.`, { duration: 6000 }),
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Could not send the test email.', { duration: 8000 }),
  });

  const tool = 'p-1.5 rounded-md border border-outline hover:border-primary hover:text-primary disabled:opacity-40';

  return (
    <div className="rounded-xl border border-outline bg-surface p-4 space-y-3 max-w-2xl">
      <div>
        <h3 className="text-sm font-bold">Your email signature</h3>
        <p className="text-xs text-on-surface-variant">
          Added under every email you send — quotations, invoices and other documents, and messages written here.
          Copy your signature from Gmail, Outlook, Word or anywhere else and paste it below; formatting, links and
          logos are kept. You can also paste or add an image.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" title="Bold" aria-label="Bold" className={tool} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('bold')}><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" title="Italic" aria-label="Italic" className={tool} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('italic')}><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" title="Underline" aria-label="Underline" className={tool} onMouseDown={(e) => e.preventDefault()} onClick={() => cmd('underline')}><Underline className="h-3.5 w-3.5" /></button>
        <button type="button" title="Link" aria-label="Add link" className={tool} onMouseDown={(e) => e.preventDefault()} onClick={addLink}><Link2 className="h-3.5 w-3.5" /></button>
        <button type="button" title="Add image (logo, photo)" aria-label="Add image" className={tool} onClick={() => fileInput.current?.click()}><ImagePlus className="h-3.5 w-3.5" /></button>
        <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden"
          onChange={(e) => { void insertImages(Array.from(e.target.files || [])); e.target.value = ''; }} />
        <button type="button" title="Clear signature" aria-label="Clear signature" className={tool} onClick={clearAll}><Eraser className="h-3.5 w-3.5" /></button>
      </div>

      <div className="relative">
        <div
          ref={box}
          role="textbox"
          aria-multiline="true"
          aria-label="Email signature"
          contentEditable
          suppressContentEditableWarning
          onInput={changed}
          onPaste={onPaste}
          className="min-h-[140px] max-h-[420px] overflow-auto rounded-lg border border-outline bg-white p-3 text-sm leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/40 [&_img]:inline-block [&_img]:max-w-full"
        />
        {empty && (
          <div className="pointer-events-none absolute left-3 top-3 text-sm text-slate-400">
            Paste your signature here, or type it…
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {dirty && <span className="mr-auto text-[11px] text-on-surface-variant">Unsaved changes</span>}
        <button onClick={() => test.mutate()} disabled={test.isPending || dirty || !canSend}
          title={!canSend ? 'Your mailbox is not set up for sending yet' : dirty ? 'Save the signature first' : undefined}
          className="px-3 py-1.5 rounded-lg border border-outline text-xs font-semibold disabled:opacity-40">
          {test.isPending ? 'Sending…' : 'Send me a test email'}
        </button>
        <button onClick={() => save.mutate()} disabled={save.isPending || !dirty}
          className="px-3 py-1.5 rounded-lg bg-primary text-on-primary text-xs font-semibold disabled:opacity-40">
          {save.isPending ? 'Saving…' : 'Save signature'}
        </button>
      </div>
    </div>
  );
}
